import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface CategoryDetailBannerStyles {
    container?: InjectableCss;
    innerWrapper?: InjectableCss;
}

export const bannerStyles: CategoryDetailBannerStyles = {
    innerWrapper: {
        css: css`
            width: 52.5%;
            margin-bottom: 63px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            padding: 30px;
                            margin-bottom: 0px;
                        `,
                        css`
                            width: 100%;
                            padding: 30px;
                            margin-bottom: 0px;
                        `,
                        css`
                            width: 100%;
                            padding: 22px;
                            margin-bottom: 63px;
                        `,
                        css`
                            width: 52.5%;
                            padding: 0px;
                            margin-bottom: 63px;
                        `,
                        css`
                            width: 52.5%;
                            padding: 0px;
                            margin-bottom: 63px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = bannerStyles;
const mapStateToProps = (state: ApplicationState) => ({
    currentLanguage: state.context.session.language,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasCategoryContext;

const CategoryDetailBanner = ({ category, currentLanguage }: Props) => {
    if (!category) {
        return null;
    }

    const colorStyles = category.properties.isDarkColorTheme === "true" ? `#33333a` : `#ffffff`;
    const slidTextAlignment =
        category.properties.bannerTextAlignment === "Left"
            ? `flex-start`
            : category.properties.bannerTextAlignment === "Right"
            ? `flex-end`
            : `center`;
    const verticalAligment = category.properties.verticalAliment === "Top" ? `flex-start` : `center`;

    const containerStyles = {
        css: css`
            background-image: url(${category.properties.customBanner});
            min-height: 497px;
            justify-content: center;
            background-size: cover;
            display: flex;
            justify-content: center;
            align-items: ${verticalAligment};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 388px;
                        `,
                        css`
                            min-height: 388px;
                        `,
                        css`
                            min-height: 388px;
                        `,
                        css`
                            min-height: 497px;
                        `,
                        css`
                            min-height: 497px;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    const bannerTextStyles = {
        css: css`
            color: ${colorStyles};
            font-size: 44px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 53px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 36px;
                            font-weight: 300;
                            letter-spacing: 0;
                            line-height: 43px;
                        `,
                        css`
                            font-size: 36px;
                            font-weight: 300;
                            letter-spacing: 0;
                            line-height: 43px;
                        `,
                        css`
                            font-size: 36px;
                            font-weight: 300;
                            letter-spacing: 0;
                            line-height: 43px;
                        `,
                        css`
                            font-size: 44px;
                            font-weight: 300;
                            letter-spacing: 0;
                            line-height: 53px;
                        `,
                        css`
                            font-size: 44px;
                            font-weight: 300;
                            letter-spacing: 0;
                            line-height: 53px;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    const outerStyles = {
        css: css`
            max-width: 1140px;
            margin: 0 auto;
            width: 100%;
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100%;
                        `,
                        css`
                            max-width: 100%;
                        `,
                        css`
                            justify-content: center;
                            align-items: center;
                            max-width: 720px;
                        `,
                        css`
                            justify-content: ${slidTextAlignment};
                            align-items: ${slidTextAlignment};
                            max-width: 960px;
                        `,
                        css`
                            justify-content: ${slidTextAlignment};
                            align-items: ${slidTextAlignment};
                            max-width: 1140px;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    const mainHeading =
        category.properties.verticalAliment === "Top"
            ? {
                  css: css`
                      color: ${colorStyles};
                      font-size: 18px;
                      font-weight: bold;
                      letter-spacing: 1.08px;
                      line-height: 25px;

                      ${({ theme }: { theme: BaseTheme }) =>
                          breakpointMediaQueries(
                              theme,
                              [
                                  null,
                                  null,
                                  null,
                                  css`
                                      padding-top: 15px;
                                  `,
                                  css`
                                      padding-top: 15px;
                                  `,
                              ],
                              "min",
                          )}
                  `,
              }
            : {
                  css: css`
                      color: ${colorStyles};
                      font-size: 18px;
                      font-weight: bold;
                      letter-spacing: 1.08px;
                      line-height: 25px;
                  `,
              };

    return (
        <StyledWrapper {...containerStyles}>
            <StyledWrapper {...outerStyles}>
                <StyledWrapper {...styles.innerWrapper}>
                    <Typography as="h3" {...mainHeading}>
                        {category.shortDescription}
                    </Typography>
                    {category.properties.customBannerTextUS && currentLanguage?.languageCode.toLowerCase() === "en" && (
                        <Typography as="p" {...bannerTextStyles}>
                            {category.properties.customBannerTextUS}
                        </Typography>
                    )}
                    {category.properties.customBannerTextES && currentLanguage?.languageCode.toLowerCase() === "es" && (
                        <Typography as="p" {...bannerTextStyles}>
                            {category.properties.customBannerTextES}
                        </Typography>
                    )}
                    {category.properties.customBannerTextFR && currentLanguage?.languageCode.toLowerCase() === "fr" && (
                        <Typography as="p" {...bannerTextStyles}>
                            {category.properties.customBannerTextFR}
                        </Typography>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryDetailBanner)),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
